import { ref, uploadBytesResumable, getDownloadURL, listAll, deleteObject } from 'firebase/storage';
import { storage } from '../config/firebase';

/**
 * Resizes and compresses an image using the HTML5 Canvas API.
 * @param file The original image file
 * @param maxWidth The maximum width of the resulting image
 * @param maxHeight The maximum height of the resulting image
 * @param quality The JPEG quality (0 to 1)
 * @returns A Promise that resolves to a Blob representing the compressed image
 */
export const compressImage = (file: File, maxWidth = 800, maxHeight = 800, quality = 0.8): Promise<Blob> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target?.result as string;
            img.onload = () => {
                let width = img.width;
                let height = img.height;

                // Calculate the new dimensions while preserving aspect ratio
                if (width > height) {
                    if (width > maxWidth) {
                        height = Math.round((height * maxWidth) / width);
                        width = maxWidth;
                    }
                } else {
                    if (height > maxHeight) {
                        width = Math.round((width * maxHeight) / height);
                        height = maxHeight;
                    }
                }

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error("Failed to get canvas context"));
                    return;
                }

                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            resolve(blob);
                        } else {
                            reject(new Error("Canvas to Blob failed"));
                        }
                    },
                    'image/jpeg',
                    quality
                );
            };
            img.onerror = (error) => reject(error);
        };
        reader.onerror = (error) => reject(error);
    });
};

/**
 * Deletes all existing profile pictures for a user from Firebase Storage.
 * Called automatically before uploading a new avatar to prevent storage bloat.
 * 
 * @param userId The UID of the user whose old avatars should be deleted
 */
export const deleteOldAvatars = async (userId: string): Promise<void> => {

    try {
        const folderRef = ref(storage, `profile-pictures/${userId}`);
        const fileList = await listAll(folderRef);

        // Delete all existing avatar files in parallel
        const deletePromises = fileList.items.map(itemRef => deleteObject(itemRef));
        await Promise.all(deletePromises);

        if (fileList.items.length > 0) {
            // Old avatars cleaned up successfully
        }
    } catch {
        // Non-critical: don't block the new upload
    }
};

/**
 * Uploads a profile picture to Firebase Storage and returns the download URL.
 * Automatically compresses the image before upload and deletes any previous avatars.
 * 
 * @param userId The UID of the user
 * @param file The image file to upload
 * @param onProgress Optional callback for upload progress (0-100)
 * @returns A Promise resolving to the download URL
 */
export const uploadProfilePicture = async (
    userId: string,
    file: File,
    onProgress?: (progress: number) => void
): Promise<string> => {

    try {
        // Delete old avatars before uploading the new one
        await deleteOldAvatars(userId);

        // Compress the image before uploading
        const compressedBlob = await compressImage(file, 400, 400, 0.8);
        
        // Generate a unique filename based on the current timestamp so browser cache invalidates
        const filename = `avatar_${Date.now()}.jpg`;
        const storageRef = ref(storage, `profile-pictures/${userId}/${filename}`);

        const uploadTask = uploadBytesResumable(storageRef, compressedBlob);

        return new Promise((resolve, reject) => {
            uploadTask.on(
                'state_changed',
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    if (onProgress) {
                        onProgress(progress);
                    }
                },
                (error) => {
                    reject(error);
                },
                async () => {
                    try {
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                        resolve(downloadURL);
                    } catch (error) {
                        reject(error);
                    }
                }
            );
        });
    } catch (error) {
        throw error;
    }
};
